import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  createPublicityApi,
  deletePublicityApi,
  fetchPublicities,
  fetchPublicityById,
  updatePublicityApi,
  validatePublicityApi,
} from "../services/publicitiesService";
import { resolveApiErrorMessage } from "../utils/resolveApiErrorMessage";

const PUBLICITIES_QUERY_KEY = ["publicities"];

export const usePublicities = () =>
  useQuery({
    queryKey: PUBLICITIES_QUERY_KEY,
    queryFn: () => fetchPublicities(),
  });

export const usePublicity = (id, options = {}) =>
  useQuery({
    queryKey: [...PUBLICITIES_QUERY_KEY, id],
    queryFn: () => fetchPublicityById(id),
    enabled: Boolean(id),
    ...options,
  });

export const useCreatePublicity = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: createPublicityApi,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PUBLICITIES_QUERY_KEY });
      toast.success("Publicité bien ajoutée", { autoClose: 3000 });
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err, "Publicité non ajoutée"), {
        autoClose: 3000,
      });
    },
  });
};

export const useUpdatePublicity = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }) => updatePublicityApi(id, payload),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: PUBLICITIES_QUERY_KEY });
      if (variables?.id) {
        qc.invalidateQueries({
          queryKey: [...PUBLICITIES_QUERY_KEY, variables.id],
        });
      }
      toast.success("Publicité bien modifiée", { autoClose: 3000 });
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err, "Publicité non modifiée"), {
        autoClose: 3000,
      });
    },
  });
};

export const useDeletePublicity = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: deletePublicityApi,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PUBLICITIES_QUERY_KEY });
      toast.success("Publicité supprimée avec succès", { autoClose: 3000 });
    },
    onError: (err) => {
      toast.error(
        resolveApiErrorMessage(
          err,
          "Une erreur est survenue lors de la suppression de la publicité"
        ),
        { autoClose: 3000 }
      );
    },
  });
};

export const usePublishPublicity = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id }) => validatePublicityApi(id),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: PUBLICITIES_QUERY_KEY });
      if (variables?.id) {
        qc.invalidateQueries({
          queryKey: [...PUBLICITIES_QUERY_KEY, variables.id],
        });
      }
      toast.success("Publicité validée avec succes", { autoClose: 3000 });
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err, "Publicité non validée"), {
        autoClose: 3000,
      });
    },
  });
};
